import { useEffect, useCallback, useRef } from "react";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import { payment, enrollment, service } from "../../../../../store";
import {
  PaymentResultLayout,
  PaymentSuccessIcon,
} from "../paymentResult";
import { trackPurchase } from "../../../../../utils/analytics";


export function SuccessPage() {
  const [searchParams] = useSearchParams();
  const confirmPayment = payment((state) => state.confirmPayment);
  const createEnrollment = enrollment((state) => state.createEnrollment);
  const getService = service((state) => state.getService);
  const { course_id, user_id } = useParams();
  const navigate = useNavigate();

  const hasRunEffect = useRef(false);

  const handlePaymentConfirm = useCallback(async () => {
    if (hasRunEffect.current) return;
    hasRunEffect.current = true;

    const orderId = searchParams.get("orderId");
    const paymentKey = searchParams.get("paymentKey");
    const amount = parseInt(searchParams.get("amount"), 10);

    // confirmPayment는 실패 시 자체적으로 alert를 띄우고 false를 반환한다 (throw하지 않음).
    const paymentSuccess = await confirmPayment(orderId, paymentKey, amount);
    if (!paymentSuccess) {
      navigate(`/courses/${course_id}`, { replace: true });
      return;
    }

    // 결제 성공했으므로 실패 기록용으로 보관하던 주문번호를 정리한다.
    sessionStorage.removeItem("pending_payment_order_id");

    const enrollmentData = {
      user_id: parseInt(user_id, 10),
      course_id: parseInt(course_id, 10),
    };

    // 결제는 이미 완료된 상태이므로, 일시적 오류로 등록이 누락되지 않도록 몇 차례 재시도한다.
    const ENROLLMENT_RETRY_COUNT = 3;
    let enrollmentSuccess = null;
    for (let attempt = 1; attempt <= ENROLLMENT_RETRY_COUNT; attempt++) {
      enrollmentSuccess = await createEnrollment(enrollmentData);
      if (enrollmentSuccess) break;
      if (attempt < ENROLLMENT_RETRY_COUNT) {
        await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
      }
    }

    if (!enrollmentSuccess) {
      console.error(
        `[결제-등록 불일치] 결제 완료 후 수강 등록 실패: orderId=${orderId}, user_id=${user_id}, course_id=${course_id}`
      );
      alert(
        `결제는 완료되었지만 수강 등록에 실패했습니다.\n주문번호: ${orderId}\n이 번호로 고객센터에 문의해주시면 등록을 도와드리겠습니다.`
      );
      navigate(`/courses/${course_id}`, { replace: true });
      return;
    }

    try {
      const courseData = await getService(course_id);
      trackPurchase(orderId, amount, courseData);
      const firstLectureId = courseData?.sections?.[0]?.lectures?.[0]?.id;

      if (firstLectureId) {
        navigate(`/courses/${course_id}/${firstLectureId}`, { replace: true });
      } else {
        navigate(`/courses/${course_id}`, { replace: true });
      }
    } catch (error) {
      // 수강 등록 자체는 이미 성공했으므로, 강의 정보 조회 실패는 결제 오류로 보이지 않게 조용히 처리한다.
      console.error("강의 정보 조회 중 오류 발생 (등록은 완료됨):", error);
      navigate(`/courses/${course_id}`, { replace: true });
    }
  }, [searchParams, confirmPayment, createEnrollment, getService, course_id, user_id, navigate]);

  useEffect(() => {
    handlePaymentConfirm();
  }, [handlePaymentConfirm]);

  return (
    <PaymentResultLayout
      icon={<PaymentSuccessIcon />}
      title="결제가 완료되었습니다"
      subtitle="잠시 후 강의 화면으로 이동합니다..."
    />
  );
}
